import { isAxiosError } from 'axios';

/** Detalhes de erro (`errors`): ModelState (`campo → string[]`) e/ou `Messages`. */
interface ApiErrorDetails {
  // biome-ignore lint/style/useNamingConvention: nome de campo definido pelo contrato do backend
  readonly Messages?: unknown;
  readonly [key: string]: unknown;
}

/** Formatos de corpo de erro retornados pelo backend ASP.NET Core. */
interface ApiErrorBody {
  readonly errors?: ApiErrorDetails;
  readonly message?: unknown;
  // biome-ignore lint/style/useNamingConvention: nome de campo definido pelo contrato do backend
  readonly Messages?: unknown;
}

/** Retorna a primeira string não vazia de um valor (string ou array aninhado). */
function firstString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim() !== '') return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = firstString(item);
      if (found) return found;
    }
  }
  return undefined;
}

/** Extrai o corpo de erro de um `AxiosError` ou de um objeto já desembrulhado. */
function extractBody(error: unknown): ApiErrorBody | undefined {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    return data && typeof data === 'object' ? (data as ApiErrorBody) : undefined;
  }
  // `Error` do JS tem `message` técnico — não é um corpo de API utilizável.
  if (error && typeof error === 'object' && !(error instanceof Error)) {
    if ('errors' in error || 'Messages' in error || 'message' in error) {
      return error as ApiErrorBody;
    }
  }
  return undefined;
}

/**
 * Extrai uma mensagem de erro legível de uma resposta de API, cobrindo os
 * formatos comuns do backend ASP.NET Core:
 *
 * - `errors.Messages`: mensagens de negócio agrupadas (ex.: validações de regra).
 * - `errors` como ModelState: dicionário `campo → string[]` de validação padrão.
 * - `Messages` / `message`: mensagens no nível raiz.
 *
 * Retorna `fallback` quando nenhuma mensagem específica é encontrada. O `title`
 * genérico do ASP.NET ("One or more validation errors occurred.") é
 * deliberadamente ignorado — prefere-se o `fallback` localizado.
 *
 * @param error - Erro capturado (normalmente um `AxiosError`)
 * @param fallback - Mensagem exibida quando não há mensagem específica
 * @returns Mensagem de erro legível
 *
 * @example
 * ```ts
 * onError: error =>
 *   alert({ alertType: 'warning', title: getApiErrorMessage(error, t('publish.toast.publishError')) });
 * ```
 */
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const body = extractBody(error);
  if (!body) return fallback;

  const { errors } = body;
  if (errors && typeof errors === 'object') {
    const fromMessages = firstString(errors.Messages);
    if (fromMessages) return fromMessages;
    for (const value of Object.values(errors)) {
      const found = firstString(value);
      if (found) return found;
    }
  }

  return firstString(body.Messages) ?? firstString(body.message) ?? fallback;
}
